import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  Users, 
  CheckCircle, 
  Clock, 
  Plus, 
  Edit2, 
  Trash2, 
  Star, 
  Phone, 
  AlertCircle, 
  X, 
  ChevronRight,
  Sparkles,
  Search,
  Filter,
  ArrowUpRight,
  ShieldCheck,
  History,
  RefreshCw,
  Database
} from 'lucide-react';
import { StoreLocation } from '../types';
import { 
  getStoreLocations, 
  saveStoreLocation, 
  setPrimaryLocation, 
  toggleLocationStatus, 
  deleteStoreLocation,
  checkHistoricalBackfillStatus,
  runHistoricalDataBackfill,
  HistoricalBackfillStatus
} from '../services/storeLocationService';
import { useStoreLocation } from '../contexts/LocationContext';
import { useSubscription } from '../hooks/useSubscription';

interface Props {
  tenantId: string;
}

const COMMON_TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

const COMMON_REGIONS = [
  'North Region',
  'South Region',
  'East Region',
  'West Region',
  'Central Region',
  'Metro Hub',
];

export const StoreLocationsSettings: React.FC<Props> = ({ tenantId }) => {
  const navigate = useNavigate();
  const sub = useSubscription(tenantId);
  const { refreshLocations } = useStoreLocation();
  const [locations, setLocations] = useState<StoreLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Subscription capacity calculations
  // Base: 1 location | Enhanced: Up to 3 locations | Enterprise: Up to 5 locations | Trial: Up to 5 locations
  const locationLimit = sub.isTrialing || sub.planId === 'enterprise'
    ? 5
    : sub.planId === 'enhanced'
    ? 3
    : 1;

  const isAtLimit = !sub.isTrialing && locations.length >= locationLimit;

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<StoreLocation | null>(null);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Historical Data Backfill States
  const [backfillStatus, setBackfillStatus] = useState<HistoricalBackfillStatus | null>(null);
  const [checkingBackfill, setCheckingBackfill] = useState(false);
  const [runningBackfill, setRunningBackfill] = useState(false);
  const [targetBackfillLocId, setTargetBackfillLocId] = useState<string>('');

  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    region: 'North Region',
    timezone: 'America/Los_Angeles',
    phone: '',
    status: 'active' as 'active' | 'inactive',
    isPrimary: false,
  });

  const loadBackfillStatus = async () => {
    if (!tenantId) return;
    try {
      setCheckingBackfill(true);
      const status = await checkHistoricalBackfillStatus(tenantId);
      setBackfillStatus(status);
      if (status.primaryLocation && !targetBackfillLocId) {
        setTargetBackfillLocId(status.primaryLocation.id);
      }
    } catch (err) {
      console.warn('Failed to check historical backfill status', err);
    } finally {
      setCheckingBackfill(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await getStoreLocations(tenantId);
      setLocations(data);
      const primary = data.find(d => d.isPrimary) || data[0];
      if (primary && !targetBackfillLocId) {
        setTargetBackfillLocId(primary.id);
      }
    } catch (err) {
      console.error('Failed to load store locations', err);
      setNotification({ type: 'error', message: 'Could not load store locations. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadBackfillStatus();
  }, [tenantId]);

  const handleRunBackfill = async () => {
    const chosenLocId = targetBackfillLocId || primaryStore?.id || locations[0]?.id;
    if (!chosenLocId) {
      setNotification({ type: 'error', message: 'Please select a target store location for the backfill.' });
      return;
    }

    const targetLoc = locations.find(l => l.id === chosenLocId);
    const targetName = targetLoc ? targetLoc.name : 'Primary Store';

    try {
      setRunningBackfill(true);
      const result = await runHistoricalDataBackfill(tenantId, chosenLocId, targetName);
      setNotification({
        type: 'success',
        message: `Zero-downtime migration complete! ${result.updatedCount} historical records cleanly tagged with ${targetName}.`
      });
      await Promise.all([loadBackfillStatus(), loadData()]);
      refreshLocations();
    } catch (err) {
      console.error('Failed to run historical backfill', err);
      setNotification({ type: 'error', message: 'Failed to run migration. Please check permissions.' });
    } finally {
      setRunningBackfill(false);
    }
  };

  const handleAddClick = () => {
    if (isAtLimit) {
      setIsUpgradeModalOpen(true);
      return;
    }
    openAddModal();
  };

  const openAddModal = () => {
    setEditingLocation(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      region: 'North Region',
      timezone: 'America/Los_Angeles',
      phone: '',
      status: 'active',
      isPrimary: locations.length === 0,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (loc: StoreLocation) => {
    setEditingLocation(loc);
    setFormData({
      name: loc.name,
      code: loc.code || '',
      address: loc.address || '',
      city: loc.city || '',
      state: loc.state || '',
      zip: loc.zip || '',
      region: loc.region || 'North Region',
      timezone: loc.timezone || 'America/Los_Angeles',
      phone: loc.phone || '',
      status: loc.status || 'active',
      isPrimary: !!loc.isPrimary,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setNotification({ type: 'error', message: 'Store name is required.' });
      return;
    }

    setSaving(true);
    try {
      await saveStoreLocation(tenantId, {
        ...(editingLocation ? { id: editingLocation.id } : {}),
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase() || undefined,
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        zip: formData.zip.trim(),
        region: formData.region,
        timezone: formData.timezone,
        phone: formData.phone.trim(),
        status: formData.status,
        isPrimary: formData.isPrimary,
      });

      if (formData.isPrimary && editingLocation) {
        await setPrimaryLocation(tenantId, editingLocation.id);
      }

      await loadData();
      await refreshLocations();
      setIsModalOpen(false);
      setNotification({
        type: 'success',
        message: editingLocation
          ? `Store "${formData.name}" was updated successfully.`
          : `New store "${formData.name}" has been registered.`,
      });
      setTimeout(() => setNotification(null), 4000);
    } catch (err) {
      console.error('Error saving store location:', err);
      setNotification({ type: 'error', message: 'Failed to save store location. Check your network or permissions.' });
    } finally {
      setSaving(false);
    }
  };

  const handleMakePrimary = async (loc: StoreLocation) => {
    try {
      await setPrimaryLocation(tenantId, loc.id);
      await loadData();
      await refreshLocations();
      setNotification({ type: 'success', message: `"${loc.name}" is now the primary store.` });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error('Error making store primary:', err);
      setNotification({ type: 'error', message: 'Failed to set primary store.' });
    }
  };

  const handleToggleStatus = async (loc: StoreLocation) => {
    try {
      await toggleLocationStatus(tenantId, loc.id, loc.status);
      await loadData();
      await refreshLocations();
      setNotification({
        type: 'success',
        message: `Store "${loc.name}" status changed to ${loc.status === 'active' ? 'Inactive' : 'Active'}.`,
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error('Error toggling location status:', err);
      setNotification({ type: 'error', message: 'Failed to update store status.' });
    }
  };

  const handleDelete = async (loc: StoreLocation) => {
    if (loc.isPrimary) {
      alert('The primary store cannot be deleted. Designate another store as primary first.');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove "${loc.name}"? Transactions tied to this location will retain their historical location tag.`)) {
      return;
    }

    try {
      await deleteStoreLocation(tenantId, loc.id);
      await loadData();
      await refreshLocations();
      setNotification({ type: 'success', message: `Store "${loc.name}" removed.` });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error('Error deleting store location:', err);
      setNotification({ type: 'error', message: 'Failed to delete store location.' });
    }
  };

  // Filtered locations
  const filteredLocations = locations.filter(loc => {
    if (regionFilter !== 'all' && loc.region !== regionFilter) return false;
    if (statusFilter !== 'all' && loc.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = loc.name.toLowerCase().includes(q);
      const matchCode = (loc.code || '').toLowerCase().includes(q);
      const matchCity = (loc.city || '').toLowerCase().includes(q);
      const matchState = (loc.state || '').toLowerCase().includes(q);
      return matchName || matchCode || matchCity || matchState;
    }
    return true;
  });

  const totalStaffAssigned = locations.reduce((sum, l) => sum + (l.staffCount || 0), 0);
  const activeCount = locations.filter(l => l.status === 'active').length;
  const primaryStore = locations.find(l => l.isPrimary) || locations[0];

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`p-4 rounded-xl text-sm font-medium flex items-center justify-between transition-all ${
            notification.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
            )}
            <span>{notification.message}</span>
          </div>
          <button
            onClick={() => setNotification(null)}
            className="text-gray-400 hover:text-gray-700 cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-black text-slate-900 tracking-tight">Store Locations Hub</h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
              Multi-Unit
            </span>
            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5 ${
              sub.isTrialing
                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                : sub.planId === 'enterprise'
                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                : sub.planId === 'enhanced'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-700 border border-gray-200'
            }`}>
              <ShieldCheck size={12} className="shrink-0" />
              {sub.isTrialing
                ? 'Trial: Multi-Store Active'
                : `${sub.planId.toUpperCase()} Plan (${locations.length} of ${locationLimit} used)`}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Configure branches, POS store codes, regional groupings, and timezones for accurate tip allocation and payroll exports.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isAtLimit && (
            <button
              onClick={() => navigate('/settings/billing')}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
            >
              <Sparkles size={14} className="text-purple-600" />
              Upgrade Tier
            </button>
          )}

          <button
            onClick={handleAddClick}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-all cursor-pointer shrink-0"
          >
            <Plus size={16} />
            Add New Store
          </button>
        </div>
      </div>

      {/* High-Level Metric Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center gap-3.5">
          <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-gray-400">Total Locations</p>
            <p className="text-2xl font-black text-slate-900 mt-0.5">{locations.length}</p>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center gap-3.5">
          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-gray-400">Active Stores</p>
            <p className="text-2xl font-black text-emerald-700 mt-0.5">{activeCount}</p>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center gap-3.5">
          <div className="p-3 rounded-xl bg-indigo-50 text-indigo-600">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-gray-400">Assigned Team</p>
            <p className="text-2xl font-black text-indigo-700 mt-0.5">{totalStaffAssigned}</p>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center gap-3.5">
          <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
            <Star className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-black uppercase tracking-wider text-gray-400">Primary HQ</p>
            <p className="text-sm font-bold text-slate-900 truncate mt-0.5">
              {primaryStore ? primaryStore.name : 'None Set'}
            </p>
          </div>
        </div>
      </div>

      {/* Historical Data Backfill & Audit Migration Section */}
      <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-slate-100 text-slate-700">
              <History size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-black text-slate-900 tracking-tight">
                  Historical Data Backfill & Audit Alignment
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                  Zero-Downtime Migration
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Automatically tags older legacy transactions and shifts created before multi-store was enabled to your primary store location.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={loadBackfillStatus}
            disabled={checkingBackfill || runningBackfill}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-all cursor-pointer self-start sm:self-auto shrink-0"
          >
            <RefreshCw size={13} className={checkingBackfill ? 'animate-spin text-blue-600' : ''} />
            <span>{checkingBackfill ? 'Scanning...' : 'Re-scan Audit Logs'}</span>
          </button>
        </div>

        {/* Dynamic Status Display */}
        {checkingBackfill ? (
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-500">
            <RefreshCw size={14} className="animate-spin text-blue-600" />
            <span>Scanning historical transactions, shifts, and staff rosters in Firestore...</span>
          </div>
        ) : backfillStatus && backfillStatus.isFullyAligned ? (
          <div className="p-4 bg-emerald-50/70 rounded-xl border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 text-emerald-800 rounded-xl shrink-0">
                <CheckCircle size={18} />
              </div>
              <div>
                <p className="text-xs font-black text-emerald-900">
                  Historical Data 100% Aligned & Audited
                </p>
                <p className="text-[11px] text-emerald-700 mt-0.5">
                  All {backfillStatus.totalScanned} records across transactions, shifts, and staff are cleanly tagged to store locations. Zero untagged historical records.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-200/80 text-emerald-900 shrink-0 self-start sm:self-auto">
              <ShieldCheck size={12} />
              Audit Ready
            </span>
          </div>
        ) : (
          <div className="p-4 bg-amber-50/80 rounded-xl border border-amber-200 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <div className="p-2 bg-amber-100 text-amber-800 rounded-xl shrink-0 mt-0.5">
                  <AlertCircle size={18} />
                </div>
                <div>
                  <p className="text-xs font-black text-amber-900">
                    {backfillStatus?.unmigratedCount || 0} Legacy Record(s) Detected Without Location Tags
                  </p>
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    These older transactions and shifts were created before multi-store mode. Tagging them ensures your historical year-over-year reports and payroll audits align seamlessly.
                  </p>
                </div>
              </div>
            </div>

            {/* Breakdown Badges */}
            {backfillStatus && (
              <div className="flex flex-wrap gap-2 pt-1 text-[11px]">
                {backfillStatus.byCollection.transactions > 0 && (
                  <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 font-bold">
                    {backfillStatus.byCollection.transactions} Transactions
                  </span>
                )}
                {backfillStatus.byCollection.shifts > 0 && (
                  <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 font-bold">
                    {backfillStatus.byCollection.shifts} Shifts
                  </span>
                )}
                {backfillStatus.byCollection.cashTips > 0 && (
                  <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 font-bold">
                    {backfillStatus.byCollection.cashTips} Cash Tips
                  </span>
                )}
                {backfillStatus.byCollection.staff > 0 && (
                  <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 font-bold">
                    {backfillStatus.byCollection.staff} Staff Profiles
                  </span>
                )}
              </div>
            )}

            {/* Action controls */}
            <div className="pt-2 border-t border-amber-200/70 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-amber-900">Assign To Location:</span>
                <select
                  value={targetBackfillLocId}
                  onChange={e => setTargetBackfillLocId(e.target.value)}
                  className="px-3 py-1.5 bg-white border border-amber-300 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} {loc.isPrimary ? '(Primary HQ)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleRunBackfill}
                disabled={runningBackfill}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-sm transition-all cursor-pointer disabled:opacity-50 shrink-0"
              >
                {runningBackfill ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    <span>Migrating Records...</span>
                  </>
                ) : (
                  <>
                    <Database size={14} />
                    <span>Run Historical Backfill</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filter & Search Controls */}
      <div className="p-4 bg-gray-50/80 rounded-2xl border border-gray-200 flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search stores by name, code, or city..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Filter size={14} />
            <span className="font-bold">Region:</span>
          </div>
          <select
            value={regionFilter}
            onChange={e => setRegionFilter(e.target.value)}
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Regions</option>
            {COMMON_REGIONS.map(r => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Stores Directory List */}
      {loading ? (
        <div className="p-12 text-center text-gray-400 text-sm">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          Loading store locations...
        </div>
      ) : filteredLocations.length === 0 ? (
        <div className="p-12 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-700">No store locations match your filter</p>
          <p className="text-xs text-gray-400 mt-1">Try resetting search criteria or add a new branch.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredLocations.map(loc => (
            <div
              key={loc.id}
              className={`p-5 rounded-2xl border transition-all ${
                loc.isPrimary
                  ? 'bg-gradient-to-br from-white to-blue-50/30 border-blue-200 shadow-sm'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-black text-slate-900 tracking-tight">{loc.name}</h3>
                    {loc.isPrimary && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800">
                        <Star size={10} className="fill-amber-600 text-amber-600" />
                        Primary HQ
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        loc.status === 'active'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                      }`}
                    >
                      {loc.status || 'Active'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-gray-500 font-mono">
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px] font-bold">
                      Code: {loc.code || 'UNASSIGNED'}
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="text-gray-600 font-sans">{loc.region || 'North Region'}</span>
                  </div>
                </div>

                {/* Quick Edit Button */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEditModal(loc)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all cursor-pointer"
                    title="Edit Store Location"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(loc)}
                    className={`p-2 rounded-xl transition-all cursor-pointer ${
                      loc.isPrimary
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                    }`}
                    disabled={loc.isPrimary}
                    title={loc.isPrimary ? 'Primary store cannot be deleted' : 'Delete Location'}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Location Details Grid */}
              <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-gray-400 block text-[10px] font-black uppercase tracking-wider">Address</span>
                  <p className="text-gray-700 mt-0.5 flex items-start gap-1">
                    <MapPin size={12} className="text-gray-400 shrink-0 mt-0.5" />
                    <span className="truncate">
                      {loc.address ? `${loc.address}, ${loc.city || ''} ${loc.state || ''}` : 'No street address'}
                    </span>
                  </p>
                </div>

                <div>
                  <span className="text-gray-400 block text-[10px] font-black uppercase tracking-wider">Timezone</span>
                  <p className="text-gray-700 mt-0.5 flex items-center gap-1">
                    <Clock size={12} className="text-gray-400 shrink-0" />
                    <span className="truncate">{loc.timezone || 'America/Los_Angeles'}</span>
                  </p>
                </div>

                <div>
                  <span className="text-gray-400 block text-[10px] font-black uppercase tracking-wider">Staff Members</span>
                  <p className="text-slate-900 font-bold mt-0.5 flex items-center gap-1">
                    <Users size={12} className="text-indigo-500 shrink-0" />
                    <span>{loc.staffCount || 0} Team Members</span>
                  </p>
                </div>

                <div>
                  <span className="text-gray-400 block text-[10px] font-black uppercase tracking-wider">Phone</span>
                  <p className="text-gray-700 mt-0.5 flex items-center gap-1">
                    <Phone size={12} className="text-gray-400 shrink-0" />
                    <span className="truncate">{loc.phone || '—'}</span>
                  </p>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                {!loc.isPrimary ? (
                  <button
                    onClick={() => handleMakePrimary(loc)}
                    className="text-[11px] font-bold text-amber-700 hover:text-amber-800 hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <Star size={12} />
                    Set as Primary Store
                  </button>
                ) : (
                  <span className="text-[11px] font-bold text-amber-800 flex items-center gap-1">
                    <Star size={12} className="fill-amber-600" />
                    Active Organization HQ
                  </span>
                )}

                <button
                  onClick={() => handleToggleStatus(loc)}
                  className={`text-[11px] font-bold cursor-pointer hover:underline ${
                    loc.status === 'active' ? 'text-gray-500' : 'text-emerald-600'
                  }`}
                >
                  {loc.status === 'active' ? 'Mark Inactive' : 'Activate Branch'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Add / Edit Store */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/40 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 shadow-2xl border border-gray-100 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
                  <Building2 size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">
                    {editingLocation ? 'Edit Store Location' : 'Register New Store Location'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {editingLocation ? `Update parameters for ${editingLocation.name}` : 'Add a new operating branch to your group'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Store Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Airport Terminal 2"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    POS Store Code / ID
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. LOC-SEA03"
                    value={formData.code}
                    onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-mono font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Regional Group
                  </label>
                  <select
                    value={formData.region}
                    onChange={e => setFormData({ ...formData, region: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {COMMON_REGIONS.map(r => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Store Timezone
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={e => setFormData({ ...formData, timezone: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {COMMON_TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  placeholder="e.g. 17801 International Blvd"
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    placeholder="SeaTac"
                    value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    maxLength={2}
                    placeholder="WA"
                    value={formData.state}
                    onChange={e => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium uppercase focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    ZIP
                  </label>
                  <input
                    type="text"
                    placeholder="98158"
                    value={formData.zip}
                    onChange={e => setFormData({ ...formData, zip: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Store Phone
                  </label>
                  <input
                    type="text"
                    placeholder="(206) 555-0199"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Operating Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="active">Active (Open)</option>
                    <option value="inactive">Inactive (Temporarily Closed)</option>
                  </select>
                </div>
              </div>

              <div className="pt-2">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPrimary}
                    onChange={e => setFormData({ ...formData, isPrimary: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-xs font-bold text-gray-700">
                    Set as Primary Headquarters Branch
                  </span>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-all cursor-pointer flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Store Location'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Tier Limit Modal */}
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 relative">
            <button
              onClick={() => setIsUpgradeModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 p-1 cursor-pointer"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-50 text-purple-600 rounded-2xl">
                <Building2 size={24} />
              </div>
              <div>
                <h3 className="text-lg font-black text-gray-900 tracking-tight">Location Limit Reached</h3>
                <p className="text-xs text-gray-500 font-medium">Your current subscription tier has reached its store limit.</p>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 mb-5 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-gray-500 uppercase tracking-wider">Current Plan</span>
                <span className="font-black text-gray-900 uppercase">{sub.planId} Tier</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-gray-500 uppercase tracking-wider">Locations Used</span>
                <span className="font-black text-purple-600">{locations.length} of {locationLimit} Maximum</span>
              </div>
            </div>

            <p className="text-xs text-gray-600 leading-relaxed mb-6">
              To add new restaurant branches, POS store codes, and separate payroll extracts, upgrade to a higher tier plan:
            </p>

            <div className="space-y-2.5 mb-6">
              <div className={`p-3.5 rounded-2xl border flex items-center justify-between ${
                sub.planId === 'base' ? 'border-purple-200 bg-purple-50/50' : 'border-gray-100 bg-white'
              }`}>
                <div>
                  <p className="text-xs font-black text-gray-900">Enhanced Plan ($39.99/mo)</p>
                  <p className="text-[11px] text-gray-500">Up to 3 store locations + advanced shift analytics</p>
                </div>
                {sub.planId === 'base' && (
                  <span className="px-2.5 py-1 bg-purple-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider">
                    Recommended
                  </span>
                )}
              </div>

              <div className="p-3.5 rounded-2xl border border-gray-100 bg-white flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-gray-900">Enterprise Plan ($79.99/mo)</p>
                  <p className="text-[11px] text-gray-500">Up to 5 store locations + Dedicated Support & Onboarding</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setIsUpgradeModalOpen(false)}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                Maybe Later
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsUpgradeModalOpen(false);
                  navigate('/settings/billing');
                }}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md shadow-purple-500/20 transition-all cursor-pointer flex items-center gap-1.5"
              >
                <span>View Plans & Upgrade</span>
                <ArrowUpRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
